<template>
  <div class="team-show">
    <div class="stk-px-6 stk-pb-4">
      <Button
        :to="{ name: 'teams-index'}"
        icon-before="fas fa-arrow-left"
        spacing="tight"
        size="3"
      >
        Back
      </Button>
    </div>

    <Title
      v-if="team?.doesNotExist"
      class="stk-px-6"
    >
      No Team found.
    </Title>

    <div v-else-if="team && canViewTeam">
      <Level class="stk-px-6 stk-pb-4">
        <LevelLeft>
          <Stack gap="2">
            <Title>
              {{ team.name }}
            </Title>

            <Subtitle v-if="team.description">
              {{ team.description }}
            </Subtitle>
          </Stack>
        </LevelLeft>

        <LevelRight
          v-if="canUpdateTeam"
        >
          <Buttons no-wrap>
            <Button
              size="3_5"
              icon-before="fas fa-plus"
              variant="primary"
              @click="showMembersModal = true"
            >
              Add Members
            </Button>

            <Button
              size="3_5"
              icon-before="fas fa-edit"
              @click="showEditModal = true"
            >
              Edit Team
            </Button>

            <Button
              v-if="canDeleteTeam"
              variant="danger"
              size="3_5"
              :loading="teamsStore.isSavingTeam"
              @click="handleDelete"
            >
              Delete Team
            </Button>
          </Buttons>
        </LevelRight>
      </Level>

      <Stack
        v-if="canUpdateTeam && teamPermissions.length"
        class="stk-px-6 team-permissions stk-pb-4"
        gap="2"
      >
        <Subtitle class="team-permissions-title">
          <Icon
            class="team-permissions-icon"
            icon="fas fa-lock"
            size="3"
          />
          Permissions for any team member
        </Subtitle>

        <Stack
          class="team-subjects"
          gap="1"
        >
          <template
            v-for="perm in teamPermissions"
          >
            <Subtitle
              :key="perm.slug"
            >
              {{ perm.name }}
            </Subtitle>

            <div
              :key="perm.slug"
              class="labels"
            >
              <label
                v-for="action in actions"
                :key="action.id"
              >
                <input
                  type="checkbox"
                  :checked="perm.actions.includes(action.id)"
                  :disabled="teamsStore.isFetchingPermissions"
                  @change="() => updatePermission(action.id, perm.slug, !perm.actions.includes(action.id))"
                >
                {{ action.label }}
              </label>
            </div>
          </template>
        </Stack>
      </Stack>

      <LoaderWithText
        v-if="teamsStore.isFetching"
        class="stk-px-6"
      >
        Loading…
      </LoaderWithText>

      <template v-else>
        <template v-if="team.owner || allMembersExceptOwner.length > 0">
          <table class="stk-table">
            <thead class="stk-table-head">
              <tr>
                <th class="stk-table-head-col">
                  Name
                </th>
                <th class="stk-table-head-col">
                  Email
                </th>
                <th class="stk-table-head-col">
                  Role
                </th>
                <th
                  v-if="canUpdateTeam"
                  class="stk-table-head-col"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="team.owner">
                <td class="stk-table-cell cell-name">
                  <strong class="stk-table-cell-name">
                    {{ team.owner.name }}
                  </strong>
                </td>
                <td class="stk-table-cell cell-email">
                  {{ team.owner.email }}
                </td>
                <td class="stk-table-cell cell-role">
                  <RoleBadge
                    role="owner"
                  />
                </td>
                <td
                  v-if="canUpdateTeam"
                  class="stk-table-cell cell-remove"
                />
              </tr>
              <tr
                v-for="(member) in allMembersExceptOwner"
                :key="member.external_id"
                class="stk-table-row"
              >
                <td class="stk-table-cell cell-name">
                  <strong class="stk-table-cell-name">
                    {{ member.name }}
                  </strong>
                </td>
                <td class="stk-table-cell cell-email">
                  {{ member.email }}
                </td>
                <td class="stk-table-cell cell-role">
                  <template
                    v-if="member.memberships[0]?.role"
                  >
                    <Dropdown
                      v-if="canUpdateTeam"
                      anchor="left"
                      width="12rem"
                    >
                      <template #trigger="{ toggle }">
                        <Button
                          class="role-dropdown"
                          icon-after="fas fa-chevron-down"
                          spacing="tight"
                          :disabled="teamsStore.isUpdatingMember.includes(member.external_id)"
                          @click="toggle"
                        >
                          <RoleBadge
                            :role="member.memberships[0].role"
                          />
                        </Button>
                      </template>

                      <Menu
                        spacing="tight"
                      >
                        <MenuItem
                          v-for="role in roleOptions"
                          :key="role.value"
                          :disabled="teamsStore.isUpdatingMember.includes(member.external_id)"
                          @click="updateMemberRole(member.external_id, role.value)"
                        >
                          <RoleBadge :role="role.value" />
                        </MenuItem>
                      </Menu>
                    </Dropdown>

                    <RoleBadge
                      v-else
                      :role="member.memberships[0].role"
                    />
                  </template>
                </td>
                <td
                  v-if="canUpdateTeam"
                  class="stk-table-cell cell-remove"
                >
                  <Button
                    variant="danger"
                    size="3"
                    outlined
                    :disabled="teamsStore.isUpdatingMember.includes(member.external_id)"
                    @click="teamsStore.removeTeamMember(team.external_id, member.external_id)"
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>
        </template>

        <div
          v-else
          class="stk-px-6"
        >
          <Callout
            status="primary"
          >
            <p>No members found</p>
          </Callout>
        </div>
      </template>

      <StakeModal v-model="showEditModal">
        <TeamForm
          :team="team"
          @onClose="showEditModal = false"
        />
      </StakeModal>

      <StakeModal v-model="showMembersModal">
        <AddTeamMembers
          :team-external-id="teamExternalId"
          :members="team.members"
          :owner-external-id="team.owner?.external_id"
          @onClose="showMembersModal = false"
        />
      </StakeModal>
    </div>

    <LoaderWithText
      v-else
      class="stk-px-6"
    >
      Loading…
    </LoaderWithText>
  </div>
</template>

<script setup>
import { watch, onMounted, computed, ref } from 'vue';
import { ToastProgrammatic as Toast } from 'buefy';
import { Button, Buttons, Callout, Dropdown, Icon, Level, LevelLeft, LevelRight, LoaderWithText, Menu, MenuItem, Stack, StakeModal, Subtitle, Title } from '@stakeui';
import { useTeamsStore } from '@frontend/ui/stores/teamsStore.js';
import { useCanViewTeam, useCanUpdateTeam, useCanDeleteTeam } from '@frontend/shared/composables/useTeamPermissions.js';

import TeamForm from '../components/TeamForm.vue';
import AddTeamMembers from '../components/AddTeamMembers.vue';
import RoleBadge from '../components/RoleBadge.vue';

const teamsStore = useTeamsStore();

const showEditModal = ref(false);
const showMembersModal = ref(false);

const props = defineProps({
  teamExternalId: {
    type: String,
    required: true,
  },
});

const team = computed(() =>
  teamsStore.items.find(t => t.external_id === props.teamExternalId) || null,
);

const allMembersExceptOwner = computed(() => team.value.members.filter(m => m.external_id !== team.value.owner?.external_id));

const { canViewTeam } = useCanViewTeam(team);
const { canUpdateTeam } = useCanUpdateTeam(team);
const { canDeleteTeam } = useCanDeleteTeam(team);

const roleOptions = computed(() => [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
]);

// Handlers
const updateMemberRole = (memberExternalId, role) => {
  teamsStore.updateSingleTeamMember(props.teamExternalId, memberExternalId, role);
};

const updatePermission = (actionId, subjectSlug, add) => {
  teamsStore.updateTeamPermission(props.teamExternalId, actionId, subjectSlug, add);
};

// Async
const loadTeam = async teamExternalId => {
  await teamsStore.loadSingleTeam(teamExternalId);
};

const handleDelete = async () => {
  if (!canDeleteTeam.value || !window.confirm('Are you sure you want to delete this team?')) {
    return;
  }

  try {
    await teamsStore.deleteTeam(team.value.external_id);

    Toast.open({
      message: `Team successfully deleted.`,
      type: 'is-success',
    });

    // Ideally we'd use useRouter here
    window.location.href = '/ui/advisor/teams';
  } catch (err) {
    console.error('Team deletion failed', err);

    Toast.open({
      message: `There was an error deleting the team.`,
      type: 'is-danger',
    });
  }
};

const LABEL_MAP = {
  'view': 'Can View',
  'edit': 'Can Edit',
  'delete': 'Can  Delete',
};

const actions = computed(() => [
  { id: 'view', label: LABEL_MAP['view'] },
  { id: 'edit', label: LABEL_MAP['edit'] },
  { id: 'delete', label: LABEL_MAP['delete'] },
]);

// We need to compare the global permission team subjects
// with the team's own teamPermissions
const teamPermissions = computed(() => {
  const subjects = teamsStore.subjects;
  const permissions = team.value?.permissions;

  if (!permissions || subjects.length === 0) {
    return [];
  }

  return subjects.map(subject => {
    const { slug, name } = subject;
    const match = permissions.find(p => p.slug === subject.slug);
    const actions = match?.actions || [];

    return {
      slug,
      name,
      actions,
    };
  });
});

// Lifecyle methods
onMounted(() => loadTeam(props.teamExternalId));

watch(() => props.teamExternalId, newId => {
  if (newId) loadTeam(newId);
});
</script>

<style scoped>
.cell-email {
  width: 24rem;
}

.cell-role {
  width: 12rem;
}

.cell-remove {
  width: 8rem;
}

.role-dropdown {
  padding-left: var(--stk-control-padding-y);
}

.team-permissions-title {
  display: flex;
  align-items: center;
  gap: 0.125em;
}

.team-permissions-icon {
  opacity: 0.5;
}

.team-subjects {
  display: grid;
  grid-template-columns: 8em 1fr;
}

.team-subject {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.labels {
  display: flex;
  gap: 0.5em 1em;
  flex-wrap: wrap;
  align-items: center;

  label {
    cursor: pointer;
  }
}
</style>
